import type { ReturnReason, Shop } from "../types";

type RawReasonValue = { lang: string; text: string };
type RawRuleFlag = { available?: boolean; required?: boolean };
type RawReason = {
  id: string;
  active?: boolean;
  babackStandardReason?: string;
  values?: RawReasonValue[];
  rules?: {
    comment?: RawRuleFlag;
    photoRequired?: RawRuleFlag;
    reservedForLoggedUser?: RawRuleFlag;
    approvalRequired?: RawRuleFlag;
    skipReturnCost?: RawRuleFlag;
    skipReshipping?: RawRuleFlag;
  };
};

type ShopWithReasons = Shop & { reasons?: RawReason[] };

export type GetReturnReasonsOptions = {
  isMerchant?: boolean;
  retractationEnabled?: boolean;
  /**
   * Whether the order is still within the 14-day legal retractation window.
   * When explicitly `false`, the retractation reason is hidden — the customer
   * can only exercise their right of withdrawal during the 14 days after the
   * sale. Defaults to showing the reason when eligibility is unknown.
   */
  retractationEligible?: boolean;
};

export function getReturnReasons(
  shop: Shop,
  lang = "fr",
  options: GetReturnReasonsOptions = {},
): ReturnReason[] {
  const { isMerchant = false, retractationEnabled, retractationEligible } =
    options;
  const reasons: RawReason[] = (shop as ShopWithReasons).reasons ?? [];

  const mapped: ReturnReason[] = reasons
    .filter((r) => r.active !== false)
    .map((r): ReturnReason => {
      const label =
        r.values?.find((v) => v.lang === lang)?.text ??
        r.values?.[0]?.text ??
        r.id;

      return {
        id: r.id,
        label,
        requiresComment: r.rules?.comment?.required ?? false,
        requiresPhoto: r.rules?.photoRequired?.required ?? false,
        commentAvailable: r.rules?.comment?.available ?? true,
        photoAvailable: r.rules?.photoRequired?.available ?? true,
        isRetractation: r.babackStandardReason === "RETRACTION",
        reservedForLoggedUser: r.rules?.reservedForLoggedUser?.available ?? false,
        approvalRequired: r.rules?.approvalRequired?.available ?? false,
        skipReturnCost: r.rules?.skipReturnCost?.available ?? false,
        skipReshipping: r.rules?.skipReshipping?.available ?? false,
        standardCategory: r.babackStandardReason ?? null,
      };
    });

  const filtered = mapped.filter((r) => {
    if (!isMerchant && r.reservedForLoggedUser) return false;
    if (retractationEnabled === false && r.isRetractation) return false;
    if (retractationEligible === false && r.isRetractation) return false;
    return true;
  });

  return filtered.sort((a, b) => {
    if (a.isRetractation === b.isRetractation) return 0;
    return a.isRetractation ? 1 : -1;
  });
}
